import { createRef, useEffect, useState } from 'react';
import type { Step4 } from '@edwin-group/cj-graphql/generated/graphql';
import { useProfileStore } from '../store/useProfileStore';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import useTranslation from '@/hooks/i18n';
import { fetchOnBoardingStep4 } from '@/services/on-boarding/onboarding.service';

const schema = z.object({
  photo: z.instanceof(File).optional(),
  forename: z.string().default(''),
  surname: z.string().default(''),
  location: z.string().default('').optional(),
  profile: z.string().default('').optional(),
});

export type SchemaInput = z.infer<typeof schema>;

export const useProfileInfo = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [response, setResponse] = useState<Step4>({} as Step4);
  const [isLoading, setLoading] = useState<boolean>(false);
  const { user, onUpdateInfo, page } = useProfileStore();
  const fileRef = createRef<HTMLInputElement>();
  const { setValue, register, watch } = useForm({
    resolver: zodResolver(schema),
  });
  const { t } = useTranslation();
  const photo = watch('photo');
  const location = watch('location');
  const profile = watch('profile');
  const surname = watch('surname');
  const forename = watch('forename');

  const onClickProfile = (profile: string) => {
    setValue('profile', profile);
  };

  const onClickProfilePicture = () => {
    fileRef.current?.click();
  };

  const onChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setValue('photo', file);
  };

  const onSaveChanges = async () => {
    setLoading(true);
    if (isEditing) {
      await onUpdateInfo({
        photo,
        surname,
        forename,
        location,
        profile,
      });
      window.location.reload();
    }

    setIsEditing(!isEditing);
    setLoading(false);
  };

  const convertDate = (date: string = '01/01/2021') =>
    new Date(date)?.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const sourcePhoto = photo ? URL.createObjectURL(photo) : user?.photo?.url;

  useEffect(() => {
    (async () => {
      const result = await fetchOnBoardingStep4();
      setResponse(result as Step4);
    })();
  }, []);

  useEffect(() => {
    setValue('location', user.location);
    setValue('surname', user.surname);
    setValue('forename', user.forename);
    setValue('profile', user.profile?.toString());
  }, [setValue, user]);

  return {
    isEditing,
    response,
    isLoading,
    user,
    fileRef,
    photo,
    location,
    surname,
    forename,
    profile,
    sourcePhoto,
    page,
    onClickProfile,
    onClickProfilePicture,
    onChangeFile,
    onSaveChanges,
    convertDate,
    setIsEditing,
    setValue,
    register,
    t,
  };
};
